import { nameFields } from "./individualName";
import { MYKYC_API_BASE } from "../config/api";
import { ownershipKnownNodes, ownershipRowNodeIds } from "./editOwnershipPercentage";

export function getUboJurisdiction(orgJurisdiction = "uae") {
  if (orgJurisdiction === null) throw new Error("Organization jurisdiction is not loaded yet.");
  const value = String(orgJurisdiction).trim().toLowerCase();
  if (["switzerland", "ch", "che"].includes(value)) return "switzerland";
  if (["", "uae", "ae", "are", "united arab emirates"].includes(value)) return "uae";
  throw new Error(`Unsupported UBO jurisdiction: ${orgJurisdiction}`);
}

export function getUboApiUrl(orgJurisdiction) {
  const suffix = getUboJurisdiction(orgJurisdiction) === "switzerland" ? "ch" : "uae";
  return `${MYKYC_API_BASE}/myKyc/ownership/ubo/${suffix}`;
}

/**
 * Call the backend UBO assessment API.
 * Returns { ubos: [nodeId, ...], directShareholders, legalArrangements, meta }; Swiss results
 * also carry the CDB 2020 forms (`forms`, `persons`), see docs/cdb-forms-rules.md.
 */
export async function assessUbosFromTree(initialAHType, nodes, orgJurisdiction) {
  const res = await fetch(getUboApiUrl(orgJurisdiction), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ initialAHType, nodes }),
    credentials: "include",
  });
  const text = await res.text();
  let data;
  try {
    data = text ? JSON.parse(text) : null;
  } catch {
    data = { nonJsonResponse: text };
  }
  if (!res.ok || data?.ok !== true) {
    const details = Array.isArray(data?.errors) ? data.errors : [];
    const messages = [...new Set(details.map(entry => {
      const node = (nodes || []).find(candidate => String(candidate?.ID) === String(entry?.nodeId));
      const name = clean(node?.name);
      const label = name && name !== String(node?.ID) ? name : "An entry in the ownership structure";
      switch (entry?.code) {
        case "NO_ROOT": return "The client entity is missing from the ownership structure. Review the ownership details and try again.";
        case "ROOT_HAS_PARENT": return "The client entity has an invalid ownership link. Review the ownership structure and try again.";
        case "DUP_ID": return `${label}: This entry is duplicated in the ownership structure. Review its ownership links and try again.`;
        case "BAD_PARENT": return `${label}: An ownership link is missing or invalid. Check which entity this entry owns.`;
        case "INDIVIDUAL_HAS_OWNERS": return `${label}: A person is listed with shareholders of their own. Review the person/entity types and ownership links.`;
        case "OWNERSHIP_CYCLE": return `${label}: The ownership links form a loop. Correct the links and try again.`;
        case "BAD_CONTROL_EVIDENCE": return `${label}: Details supporting control are incomplete. Review the control information and try again.`;
        case "BAD_TYPE": return `${label}: Select whether this entry is a person, company, trust or foundation.`;
        default: return "The ownership structure is incomplete or contains invalid details. Review the ownership entries and try again.";
      }
    }))];
    const error = new Error(messages.join(" ") || (data?.error === "Invalid payload"
      ? "The ownership structure is incomplete or contains invalid details. Review the ownership entries and try again."
      : `Beneficial owner identification is temporarily unavailable. Please try again later. (${res.status})`));
    error.details = details;
    throw error;
  }
  return data?.jurisdiction === "switzerland" ? withSwissIdLists(data, nodes) : data;
}

/**
 * Swiss CDB forms result → the node-id lists the ownership sections read.
 * Every natural person named on a form qualifies; T/S subjects are the legal arrangements.
 */
export function withSwissIdLists(data, nodes = []) {
  const nodeById = new Map((nodes || []).map((node) => [clean(node?.ID), node]));
  const personIds = [...new Set((data?.persons || []).map((p) => clean(p?.nodeId)).filter((id) => nodeById.has(id)))];
  const isDirect = (id) => clean(nodeById.get(id)?.parentIDs?.[0]) === "X0";
  return {
    ...data,
    directShareholders: personIds.filter(isDirect),
    ubos: personIds.filter((id) => !isDirect(id)),
    legalArrangements: [...new Set((data?.forms || [])
      .filter((f) => (f.form === "T" || f.form === "S") && nodeById.has(clean(f.subjectId)))
      .map((f) => clean(f.subjectId)))],
    meta: { jurisdiction: "switzerland", reviewRequired: data?.reviewRequired === true },
  };
}

// A table date (Date, Firestore timestamp, ISO or dd.mm.yyyy text) as the forms write it: dd.mm.yyyy.
export function rowDateText(value) {
  if (!value) return "";
  let date = null;
  if (value instanceof Date) date = value;
  else if (typeof value?.toDate === "function") date = value.toDate();
  else if (typeof value?.seconds === "number") date = new Date(value.seconds * 1000);
  else if (typeof value === "string") {
    const iso = value.trim().match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (iso) return `${iso[3]}.${iso[2]}.${iso[1]}`;
    const parsed = new Date(value);
    if (!Number.isFinite(parsed.getTime())) return value.trim();
    date = parsed;
  } else if (typeof value === "number") date = new Date(value);
  if (!date || !Number.isFinite(date.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${pad(date.getDate())}.${pad(date.getMonth() + 1)}.${date.getFullYear()}`;
}

/**
 * Give trust/foundation nodes the parties recorded in ownership.legalArrangements,
 * so the Swiss engine can fill Forms T and S.
 */
export function attachArrangementParties(nodes, arrangements, { shareholders = [], knownNodes = null } = {}) {
  const list = Array.isArray(arrangements) ? arrangements : [];
  if (!list.length) return nodes;
  // Node of each arrangement by the same ID rule the ownership graph used; a trust held directly
  // is the node of its shareholder row.
  const arrangementIds = ownershipRowNodeIds(
    list.map((a) => ({ treeNodeId: clean(a?._treeArrangementNodeId || a?.treeNodeId || a?.subjectTreeNodeId) })),
    knownNodes,
    "LA_rev"
  );
  const shareholderRows = Array.isArray(shareholders) ? shareholders : [];
  const shareholderIds = ownershipRowNodeIds(shareholderRows, knownNodes, "SH_rev");
  const shareholderNodeByRowId = new Map(
    shareholderRows.map((row, index) => [clean(row?._rowId), shareholderIds[index]]).filter(([rowId]) => rowId)
  );
  const arrangementNodeIds = list.map((a, index) => [
    arrangementIds[index],
    a?.source === "shareholder" ? clean(a?.sourceRefNodeId) : "",
    a?.source === "shareholder" ? shareholderNodeByRowId.get(clean(a?.sourceRefRowId)) || "" : "",
  ].filter(Boolean));
  const typeOf = (a) => (clean(a?.arrangementType) === "Foundation" ? "Foundation" : "Trust");

  const arrangementFor = new Map();
  (nodes || []).forEach((node) => {
    const type = clean(node?.type);
    if (type !== "Trust" && type !== "Foundation") return;
    const id = clean(node?.ID);
    const index = list.findIndex((a, i) =>
      (id === "X0" && a?.source === "account_holder") || arrangementNodeIds[i].includes(id));
    if (index >= 0) arrangementFor.set(id, index);
  });
  // Legacy fallback for nodes no row points to: a name that only one unclaimed arrangement has.
  const claimed = new Set(arrangementFor.values());
  (nodes || []).forEach((node) => {
    const type = clean(node?.type);
    const id = clean(node?.ID);
    const name = clean(node?.name).toLowerCase();
    if ((type !== "Trust" && type !== "Foundation") || arrangementFor.has(id) || !name) return;
    const matches = list
      .map((a, i) => i)
      .filter((i) => !claimed.has(i) && clean(list[i]?.name).toLowerCase() === name && typeOf(list[i]) === type);
    if (matches.length === 1) {
      arrangementFor.set(id, matches[0]);
      claimed.add(matches[0]);
    }
  });

  return (nodes || []).map((node) => {
    const arrangement = list[arrangementFor.get(clean(node?.ID))];
    if (!Array.isArray(arrangement?.parties)) return node;
    // The table's parties replace those drawn in the diagram (node.roles), so a party renamed or
    // deleted in the table is not listed as well. A "class" beneficiary row stands for a class,
    // not a person named "Class (unnamed)".
    const isClassRow = (p) => /^beneficiary$/i.test(clean(p?.role)) && /^class\b|\bunnamed\b/i.test(clean(p?.name));
    const hasClass = arrangement.parties.some(isClassRow);
    const nodeClass = node?.roles?.beneficiaries?.kind === "class" ? node.roles.beneficiaries : { kind: "class" };
    // Details the table rows lack (e.g. nationality entered in the builder) are taken from the
    // same person drawn in the diagram (same role and name).
    const roles = node?.roles && typeof node.roles === "object" ? node.roles : {};
    const drawn = [];
    Object.entries(roles).forEach(([role, value]) => {
      if (role === "beneficiaries") (value?.kind === "named" ? value.list || [] : []).forEach((p) => drawn.push({ role: "beneficiary", p }));
      else [].concat(value || []).forEach((p) => p && typeof p === "object" && drawn.push({ role, p }));
    });
    const roleKey = (role) => clean(role).toLowerCase().replace(/^council.*/, "council").replace(/^further.*/, "furtherpersons");
    const drawnFor = (party) => drawn.find(({ role, p }) =>
      roleKey(role) === roleKey(party?.role) && clean(p?.name).toLowerCase() === clean(party?.name).toLowerCase())?.p || {};
    const partyDates = (date) => (date ? { dateOfBirth: date, incorporationDate: date } : {});
    const { roles: _diagramRoles, ...rest } = node;
    // While every party row is still blank, the diagram's parties stay in use.
    const tableHasParties = arrangement.parties.some((p) => clean(p?.name));
    return {
      ...(tableHasParties ? rest : node),
      ...(tableHasParties && hasClass ? { roles: { ...(node?.roles?.kind ? { kind: node.roles.kind } : {}), beneficiaries: nodeClass } } : {}),
      parties: arrangement.parties.filter((p) => !isClassRow(p)).map((p) => ({
        role: clean(p?.role),
        name: clean(p?.name) || [clean(p?.givenNames), clean(p?.familyName)].filter(Boolean).join(" "),
        nationality: clean(p?.nationality) || clean(drawnFor(p).nationality),
        domicile: clean(p?.domicile) || clean(drawnFor(p).domicile) || clean(drawnFor(p).countryOfIncorporation),
        kind: clean(p?.trusteeKind || p?.councilKind || p?.kind) || clean(drawnFor(p).kind),
        // Date of birth, or of incorporation for a company party; the form shows the one that applies.
        ...partyDates(rowDateText(p?.dobOrIncorporationDate) ||
          clean(drawnFor(p).dateOfBirth) || clean(drawnFor(p).incorporationDate)),
        ...(p?.partyType ? { partyType: clean(p.partyType) } : {}),
      })),
    };
  });
}

// initialAHType values that mean "percentages not meaningful at root level"
const PCT_NOT_MEANINGFUL_TYPES = new Set([
  "Domiciliary Company (PIC)",
  "Holding (Passive)",
  "Quoted Company",
  "Trust",
  "Foundation",
]);

function entitySkipsPercent(entityCategory, holdingKind) {
  return (
    entityCategory === "pic" ||
    (entityCategory === "holding" && holdingKind === "passive") ||
    entityCategory === "quoted"
  );
}

function capitalizeType(raw, node) {
  const t = String(raw || "").trim().toLowerCase();
  if (t === "individual") return "Individual";
  if (t === "trust") return "Trust";
  if (t === "foundation") return "Foundation";
  if (node?.entityCategory === "pic") return "PIC";
  if (node?.entityCategory === "quoted") return "Quoted Company";
  if (node?.entityCategory === "holding") return node?.holdingKind === "passive" ? "HoldingPassive" : "HoldingActive";
  return "Non Quoted Operational Company";
}

/**
 * Convert guided-chat assistantRoots (tree format) to the flat node array
 * expected by the API.  Customer name goes into X0.
 */
export function convertAssistantRootsToNodes(roots, initialAHType, customerName) {
  const nodes = [
    {
      ID: "X0",
      name: String(customerName || "").trim(),
      type: String(initialAHType || "Operating Company"),
      parentIDs: [],
      percent: 100,
    },
  ];

  const rootUsesPercent = !PCT_NOT_MEANINGFUL_TYPES.has(String(initialAHType || "").trim());

  function walk(nodeList, parentId, usesPercent) {
    if (!Array.isArray(nodeList)) return;
    nodeList.forEach((node, idx) => {
      const type = String(node?.type || "").trim().toLowerCase();
      if (!type || type === "legalarrangement") return;

      const id = String(node?.id || node?._generatedRowId || `guided_${parentId}_${idx}`);
      const name = String(node?.name || "").trim();

      // Resolve direct percent
      let pct = 100;
      if (usesPercent) {
        const siblings = nodeList;
        const isLast = idx === siblings.length - 1;
        const explicit = Number(node?.ownershipPercent);
        if (Number.isFinite(explicit) && explicit > 0) {
          pct = explicit;
        } else if (isLast) {
          const used = siblings.reduce((sum, sib, sibIdx) => {
            if (sibIdx === idx) return sum;
            return sum + (Number(sib?.ownershipPercent) || 0);
          }, 0);
          pct = Math.max(0, Math.round((100 - used) * 100) / 100);
        }
      }

      nodes.push({
        ID: id,
        ...nameFields(node),
        name,
        type: capitalizeType(type, node),
        ...(node?.effectiveControl ? { effectiveControl: node.effectiveControl } : {}),
        parentIDs: [parentId],
        // Not applicable below a PIC/passive holding (the engines count that link as 100%).
        percent: usesPercent ? pct : undefined,
        nationality:
          type === "individual"
            ? String(node?.nationality || "")
            : "",
        countryOfResidence:
          type === "individual"
            ? String(node?.countryOfResidence || node?.domicile || "")
            : String(node?.countryOfResidence || node?.countryOfIncorporation || ""),
      });

      // Recurse into entity children
      if (
        type === "entity" &&
        node?.entityCategory !== "quoted" &&
        Array.isArray(node?.owners) &&
        node.owners.length > 0
      ) {
        const childUsesPercent = !entitySkipsPercent(
          String(node?.entityCategory || ""),
          String(node?.holdingKind || "")
        );
        walk(node.owners, id, childUsesPercent);
      }
    });
  }

  walk(Array.isArray(roots) ? roots : [], "X0", rootUsesPercent);
  return nodes;
}

/**
 * Stamp meetsThreshold on existing UBO rows using a fresh API result.
 * Each row is matched to its tree node by the shared rule (ownershipRowNodeIds), the same one the
 * ownership graph used to build the API payload. `knownNodeIds`: see ownershipKnownNodes.
 */
// A row's node ID, stored on rows that have none yet (entered by hand), so the "Qualified UBO"
// badge and later matches find the box the assessment was made for.
function withTreeNodeId(row, id, payloadNodeIds) {
  if (!id || clean(row?.treeNodeId || row?.subjectTreeNodeId || row?.subjectNodeId)) return row;
  return payloadNodeIds && payloadNodeIds.has(id) ? { ...row, treeNodeId: id } : row;
}

export function applyThresholdToUboRows(uboRows, apiResult, knownNodeIds = null, payloadNodeIds = null) {
  const qualifyingIds = new Set(apiResult?.ubos || []);
  const nodeIds = ownershipRowNodeIds(uboRows || [], knownNodeIds, "UBO_rev");

  return (uboRows || []).map((row, index) => {
    const explicitNodeId = String(
      row?.treeNodeId || row?.subjectTreeNodeId || row?.subjectNodeId || ""
    ).trim();
    // A row with no node and no link to a shareholder is not in the graph (e.g. a managing director).
    if (!explicitNodeId && !row?.relatedShareholderNodeId && !row?.relatedShareholderRowId) {
      return { ...row };
    }
    return { ...withTreeNodeId(row, nodeIds[index], payloadNodeIds), meetsThreshold: qualifyingIds.has(nodeIds[index]) };
  });
}

function rowIsIndividual(row) {
  const values = [
    row?.holderType,
    row?.shareholderEntityType,
    row?.type,
    row?.partyType,
  ]
    .map((value) => String(value || "").trim().toLowerCase())
    .filter(Boolean);
  return values.some((value) => value === "individual" || value === "person");
}

/**
 * Direct shareholder document eligibility follows the same API-qualified
 * individual-node treatment used by qualified UBOs (rows matched to nodes by the shared rule).
 */
export function applyDocumentEligibilityToShareholderRows(shareholderRows, apiResult, knownNodeIds = null, payloadNodeIds = null) {
  const qualifyingIds = new Set([
    ...(apiResult?.ubos || []),
    ...(apiResult?.documentShareholders || []),
    ...(apiResult?.directShareholders || []),
  ].map((id) => clean(id)).filter(Boolean));
  const nodeIds = ownershipRowNodeIds(shareholderRows || [], knownNodeIds, "SH_rev");

  return (shareholderRows || []).map((row, index) => ({
    ...withTreeNodeId(row, nodeIds[index], payloadNodeIds),
    requiresDocuments: rowIsIndividual(row) && qualifyingIds.has(nodeIds[index]),
  }));
}

function clean(value) {
  return String(value || "").trim();
}

function stableRowIdFromNodeId(nodeId) {
  const safe = clean(nodeId).replace(/[^a-zA-Z0-9_]/g, "_");
  return safe ? `qualified_ubo_${safe}` : "";
}

function directShareholderNodeIdFor(node, nodeById) {
  const nodeId = clean(node?.ID || node?.id);
  const chain = Array.isArray(node?.parentIDs)
    ? node.parentIDs.map((id) => clean(id)).filter(Boolean)
    : [];
  const idxX0 = chain.indexOf("X0");

  if (!nodeId) return "";
  if (idxX0 === 0) return nodeId;
  if (idxX0 > 0) return chain[idxX0 - 1] || "";

  const parentId = chain[0] || "";
  const parent = parentId ? nodeById.get(parentId) : null;
  if (!parent) return "";
  return directShareholderNodeIdFor(parent, nodeById);
}

function nodeIsIndividual(node) {
  return clean(node?.type).toLowerCase() === "individual";
}

function nodeIsDirectShareholder(node) {
  const nodeId = clean(node?.ID || node?.id);
  if (!nodeId || nodeId === "X0") return false;
  const chain = Array.isArray(node?.parentIDs)
    ? node.parentIDs.map((id) => clean(id)).filter(Boolean)
    : [];
  return chain[0] === "X0";
}

/**
 * Build the canonical list of API-qualified UBO rows from the exact API input
 * and output. This lets documents, geo risk, and the identification modal rely
 * on the same assessment instead of each section reinterpreting ownership rows.
 */
export function buildQualifiedUboRowsFromAssessment({
  apiPayload,
  apiResult,
  shareholderRows = [],
  existingUboRows = [],
}) {
  const nodes = Array.isArray(apiPayload?.nodes) ? apiPayload.nodes : [];
  const nodeById = new Map(
    nodes
      .map((node) => [clean(node?.ID || node?.id), node])
      .filter(([id]) => !!id)
  );
  const qualifiedIds = new Set([
    ...(apiResult?.ubos || []),
    ...(apiResult?.documentShareholders || []),
    ...(apiResult?.directShareholders || []),
  ]
    .map((id) => clean(id))
    .filter((id) => nodeIsIndividual(nodeById.get(id))));
  if (!qualifiedIds.size) return [];

  const shareholderRowIdByNodeId = new Map(
    (Array.isArray(shareholderRows) ? shareholderRows : [])
      .map((row) => [clean(row?.treeNodeId), clean(row?._rowId)])
      .filter(([nodeId, rowId]) => !!nodeId && !!rowId)
  );
  const shareholderByNodeId = new Map();
  (Array.isArray(shareholderRows) ? shareholderRows : []).forEach((row) => {
    const nodeId = clean(row?.treeNodeId);
    if (nodeId) shareholderByNodeId.set(nodeId, row);
  });
  const existingByNodeId = new Map();
  (Array.isArray(existingUboRows) ? existingUboRows : []).forEach((row) => {
    const nodeId = clean(row?.treeNodeId || row?.subjectTreeNodeId || row?.subjectNodeId);
    if (nodeId) existingByNodeId.set(nodeId, row);
  });

  return Array.from(qualifiedIds)
    .map((nodeId) => {
      const node = nodeById.get(nodeId);
      if (!node || !nodeIsIndividual(node)) return null;

      const directShareholderRow = shareholderByNodeId.get(nodeId) || {};
      const existing = existingByNodeId.get(nodeId) || directShareholderRow || {};
      const relatedShareholderNodeId = directShareholderNodeIdFor(node, nodeById);
      const relatedShareholderRowId =
        shareholderRowIdByNodeId.get(relatedShareholderNodeId) ||
        clean(existing?.relatedShareholderRowId);

      const name = clean(existing?.name) || clean(node?.name);
      if (!name) return null;

      return {
        ...existing,
        _rowId: clean(existing?._rowId) || stableRowIdFromNodeId(nodeId),
        relatedShareholderIdx: existing?.relatedShareholderIdx || "",
        relatedShareholderNodeId:
          clean(existing?.relatedShareholderNodeId) || relatedShareholderNodeId,
        relatedShareholderRowId,
        treeNodeId: nodeId,
        subjectNodeId: clean(existing?.subjectNodeId) || nodeId,
        ...nameFields(existing.givenNames || existing.familyName ? existing : node),
        name,
        nationality: clean(existing?.nationality) || clean(node?.nationality),
        countryOfResidence:
          clean(existing?.countryOfResidence) || clean(node?.countryOfResidence),
        basisOfControl: clean(existing?.basisOfControl) || "Indirect ownership",
        effectiveOwnershipPercent:
          existing?.effectiveOwnershipPercent ??
          (Number.isFinite(Number(node?.percent)) ? Number(node.percent) : ""),
        meetsThreshold: true,
        source: "ubo_assessment",
      };
    })
    .filter(Boolean);
}

export function applyUboAssessmentResultToOwnership({
  ownership = {},
  apiPayload,
  apiResult,
  signature = "",
}) {
  const shareholderRows = Array.isArray(ownership?.shareholders)
    ? ownership.shareholders
    : Array.isArray(ownership?.directShareholders)
    ? ownership.directShareholders
    : [];
  const uboRows = Array.isArray(ownership?.ubos)
    ? ownership.ubos
    : Array.isArray(ownership?.controllingPersonsNatural)
    ? ownership.controllingPersonsNatural
    : [];

  // Match rows to nodes exactly as the ownership graph did when it built the API payload.
  const knownNodes = ownershipKnownNodes(ownership);
  const payloadNodeIds = new Set((apiPayload?.nodes || []).map((node) => clean(node?.ID || node?.id)).filter(Boolean));
  const assessedShareholderRows = applyDocumentEligibilityToShareholderRows(
    shareholderRows,
    apiResult || {},
    knownNodes,
    payloadNodeIds
  );
  const assessedUboRows = applyThresholdToUboRows(uboRows, apiResult || {}, knownNodes, payloadNodeIds);
  const qualifiedUbos = buildQualifiedUboRowsFromAssessment({
    apiPayload,
    apiResult: apiResult || {},
    shareholderRows: assessedShareholderRows,
    existingUboRows: assessedUboRows,
  });

  return {
    ...ownership,
    shareholders: assessedShareholderRows,
    directShareholders: assessedShareholderRows,
    ubos: assessedUboRows,
    controllingPersonsNatural: assessedUboRows,
    qualifiedUbos,
    uboAssessment: {
      signature,
      jurisdiction: apiResult?.meta?.jurisdiction || "uae",
      reviewRequired: apiResult?.meta?.reviewRequired === true,
      errors: apiResult?.errors || [],
      ...(Array.isArray(apiResult?.forms) ? { cdbForms: apiResult.forms } : {}),
      thresholdUboIds: [...(apiResult?.ubos || [])],
      qualifiedUboIds: qualifiedUbos.map((row) => row?.treeNodeId).filter(Boolean),
      documentShareholderIds: [
        ...(apiResult?.documentShareholders || []),
        ...(apiResult?.directShareholders || []),
      ],
      qualifiedUbos,
    },
  };
}
